import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAccount, useConfig, usePublicClient, useWalletClient } from "wagmi";
import { getConnectorClient } from "wagmi/actions";
import { polygon } from "viem/chains";
import { Address, decodeEventLog, formatEther, formatUnits, isAddress, zeroAddress } from "viem";
import { toDataURL } from "qrcode";
import { Archive, ArrowDownToLine, ArrowUpFromLine, Building2, Check, Copy, ExternalLink, Loader2, QrCode, RefreshCw, Vote } from "lucide-react";
import Header from "@/components/Header";
import { HybridIdentityPanel } from "@/components/inception/HybridIdentityPanel";
import { SessionKeyPanel } from "@/components/inception/SessionKeyPanel";
import { TrustActionsPanel } from "@/components/inception/TrustActionsPanel";
import { NestedOwnershipPanel } from "@/components/inception/NestedOwnershipPanel";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import {
  ERC1155_INTERFACE_ID,
  ERC1155_TRANSFER_ABI,
  ERC165_ABI,
  ERC721_INTERFACE_ID,
  ERC721_TRANSFER_ABI,
  ERC20_BALANCE_ABI,
  INCEPTION_ERC1155_WITHDRAW_ABI,
  INCEPTION_ERC721_WITHDRAW_ABI,
  INCEPTION_FACTORY_ABI,
  INCEPTION_FACTORY_ADDRESS,
  INCEPTION_VAULT_ABI,
} from "@/features/inception/config";
import {
  encodeDomainRegistration,
  MAX_DOMAIN_RECORD_BYTES,
  POLYGON_NAME_SERVICE_ABI,
  POLYGON_NAME_SERVICE_ADDRESS,
  POLYGON_NAME_TLD,
  validateDomainLabel,
} from "@/features/domains/config";

type AssetStandard = "ERC-721" | "ERC-1155" | "Unknown";

type VaultAsset = {
  token: Address;
  tokenId: bigint;
  standard: AssetStandard;
  balance?: bigint;
};

const transactionMessage = (error: unknown) => {
  if (!error || typeof error !== "object") return "The transaction was not completed.";
  const candidate = error as { shortMessage?: string; message?: string };
  return candidate.shortMessage || candidate.message || "The transaction was not completed.";
};

const compactAddress = (value: string) => `${value.slice(0, 6)}…${value.slice(-4)}`;

const InceptionTokens = () => {
  const { address, isConnected, connector, chainId: connectedChainId } = useAccount();
  const wagmiConfig = useConfig();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient({ chainId: polygon.id });
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isMinting, setIsMinting] = useState(false);
  const [isLoadingVaults, setIsLoadingVaults] = useState(false);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [isTransacting, setIsTransacting] = useState(false);
  const [vaults, setVaults] = useState<Address[]>([]);
  const [selectedVault, setSelectedVault] = useState<Address>();
  const [assets, setAssets] = useState<VaultAsset[]>([]);
  const [nativeBalance, setNativeBalance] = useState<bigint>(0n);
  const [supportsRecovery, setSupportsRecovery] = useState(false);
  const [supportsAgentAccount, setSupportsAgentAccount] = useState(false);
  const [erc20Token, setErc20Token] = useState("");
  const [erc20Balance, setErc20Balance] = useState<bigint>();
  const [erc20Decimals, setErc20Decimals] = useState(18);
  const [erc20Symbol, setErc20Symbol] = useState("tokens");
  const [depositToken, setDepositToken] = useState("");
  const [depositTokenId, setDepositTokenId] = useState("");
  const [depositAmount, setDepositAmount] = useState("1");
  const [depositStandard, setDepositStandard] = useState<"erc721" | "erc1155">("erc721");
  const [withdrawRecipient, setWithdrawRecipient] = useState("");
  const [withdrawAmounts, setWithdrawAmounts] = useState<Record<string, string>>({});
  const [copiedVault, setCopiedVault] = useState(false);
  const [depositQrCode, setDepositQrCode] = useState<string>();
  const [depositQrPayload, setDepositQrPayload] = useState<string>();
  const [domainLabel, setDomainLabel] = useState("");
  const [domainRecord, setDomainRecord] = useState("");

  const requireWallet = useCallback(async () => {
    if (!isConnected || !address || !connector) throw new Error("Connect your wallet before continuing.");
    if (!publicClient) throw new Error("The Polygon RPC client is unavailable. Refresh the page and try again.");

    let actualChainId = connectedChainId;
    try {
      actualChainId = await connector.getChainId();
    } catch {
      // Use Wagmi's reactive chain ID if the connector cannot answer directly.
    }
    if (actualChainId !== polygon.id) throw new Error(`Switch your wallet to Polygon (chain 137). It is currently connected to chain ${actualChainId ?? "unknown"}.`);

    let client = walletClient;
    if (!client) {
      try {
        client = await getConnectorClient(wagmiConfig, {
          account: address,
          chainId: polygon.id,
          connector,
        }) as NonNullable<typeof walletClient>;
      } catch {
        throw new Error("The wallet is connected, but its signer is unavailable. Unlock the wallet, then disconnect and reconnect it.");
      }
    }
    return { account: address, client, reader: publicClient };
  }, [address, connectedChainId, connector, isConnected, publicClient, wagmiConfig, walletClient]);

  const loadVaults = useCallback(async () => {
    if (!address || !publicClient || !INCEPTION_FACTORY_ADDRESS) {
      setVaults([]);
      setSelectedVault(undefined);
      return;
    }
    setIsLoadingVaults(true);
    try {
      const balance = await publicClient.readContract({
        address: INCEPTION_FACTORY_ADDRESS,
        abi: INCEPTION_FACTORY_ABI,
        functionName: "balanceOf",
        args: [address],
      } as never) as bigint;
      const ownedVaults = await Promise.all(
        Array.from({ length: Number(balance) }, async (_, index) => {
          const tokenId = await publicClient.readContract({
            address: INCEPTION_FACTORY_ADDRESS,
            abi: INCEPTION_FACTORY_ABI,
            functionName: "tokenOfOwnerByIndex",
            args: [address, BigInt(index)],
          } as never) as bigint;
          return publicClient.readContract({
            address: INCEPTION_FACTORY_ADDRESS,
            abi: INCEPTION_FACTORY_ABI,
            functionName: "collectionAddress",
            args: [tokenId],
          } as never) as Promise<Address>;
        }),
      );
      setVaults(ownedVaults);
      setSelectedVault((current) => current && ownedVaults.includes(current) ? current : ownedVaults[0]);
    } catch (error) {
      toast({ title: "Unable to load Inception Tokens", description: transactionMessage(error), variant: "destructive" });
    } finally {
      setIsLoadingVaults(false);
    }
  }, [address, publicClient, toast]);

  const loadAssets = useCallback(async () => {
    if (!selectedVault || !publicClient) {
      setAssets([]);
      setNativeBalance(0n);
      setSupportsRecovery(false);
      setSupportsAgentAccount(false);
      return;
    }
    setIsLoadingAssets(true);
    try {
      const [count, balance, version, accountVersion] = await Promise.all([
        publicClient.readContract({
          address: selectedVault,
          abi: INCEPTION_VAULT_ABI,
          functionName: "ownedAssetsCount",
        } as never) as Promise<bigint>,
        publicClient.getBalance({ address: selectedVault }),
        publicClient.readContract({
          address: selectedVault,
          abi: INCEPTION_VAULT_ABI,
          functionName: "vaultVersion",
        } as never).catch(() => 1n) as Promise<bigint>,
        publicClient.readContract({
          address: selectedVault,
          abi: INCEPTION_VAULT_ABI,
          functionName: "accountVersion",
        } as never).catch(() => 0n) as Promise<bigint>,
      ]);
      const vaultAssets = await Promise.all(
        Array.from({ length: Number(count) }, async (_, index): Promise<VaultAsset> => {
          const [token, tokenId] = await publicClient.readContract({
            address: selectedVault,
            abi: INCEPTION_VAULT_ABI,
            functionName: "ownedAssetAt",
            args: [BigInt(index)],
          } as never) as readonly [Address, bigint];
          const supports = async (interfaceId: `0x${string}`) => {
            try {
              return await publicClient.readContract({ address: token, abi: ERC165_ABI, functionName: "supportsInterface", args: [interfaceId] } as never) as boolean;
            } catch {
              return false;
            }
          };
          if (await supports(ERC721_INTERFACE_ID)) return { token, tokenId, standard: "ERC-721" };
          if (await supports(ERC1155_INTERFACE_ID)) {
            const balance = await publicClient.readContract({
              address: token,
              abi: ERC1155_TRANSFER_ABI,
              functionName: "balanceOf",
              args: [selectedVault, tokenId],
            } as never) as bigint;
            return { token, tokenId, standard: "ERC-1155", balance };
          }
          return { token, tokenId, standard: "Unknown" };
        }),
      );
      setAssets(vaultAssets);
      setNativeBalance(balance);
      setSupportsRecovery(version >= 2n);
      setSupportsAgentAccount(accountVersion >= 2n);
    } catch (error) {
      toast({ title: "Unable to load vault assets", description: transactionMessage(error), variant: "destructive" });
    } finally {
      setIsLoadingAssets(false);
    }
  }, [publicClient, selectedVault, toast]);

  useEffect(() => { void loadVaults(); }, [loadVaults]);
  useEffect(() => { void loadAssets(); }, [loadAssets]);

  const loadErc20Balance = useCallback(async () => {
    if (!selectedVault || !publicClient || !isAddress(erc20Token)) {
      setErc20Balance(undefined);
      return;
    }
    try {
      const token = erc20Token as Address;
      const [balance, decimals, symbol] = await Promise.all([
        publicClient.readContract({ address: token, abi: ERC20_BALANCE_ABI, functionName: "balanceOf", args: [selectedVault] } as never) as Promise<bigint>,
        publicClient.readContract({ address: token, abi: ERC20_BALANCE_ABI, functionName: "decimals" } as never).catch(() => 18) as Promise<number>,
        publicClient.readContract({ address: token, abi: ERC20_BALANCE_ABI, functionName: "symbol" } as never).catch(() => "tokens") as Promise<string>,
      ]);
      setErc20Balance(balance);
      setErc20Decimals(Number(decimals));
      setErc20Symbol(String(symbol));
    } catch {
      setErc20Balance(undefined);
    }
  }, [erc20Token, publicClient, selectedVault]);

  useEffect(() => { void loadErc20Balance(); }, [loadErc20Balance]);

  useEffect(() => {
    let active = true;
    const buildQrCode = async () => {
      if (!address || !selectedVault || !isAddress(depositToken) || !/^\d+$/.test(depositTokenId) || (depositStandard === "erc1155" && !/^\d+$/.test(depositAmount))) {
        setDepositQrCode(undefined);
        setDepositQrPayload(undefined);
        return;
      }
      const parameters = new URLSearchParams();
      parameters.append("address", address);
      parameters.append("address", selectedVault);
      parameters.append("uint256", depositTokenId);
      if (depositStandard === "erc1155") {
        parameters.append("uint256", depositAmount);
        parameters.append("bytes", "0x");
      }
      const payload = `ethereum:${depositToken}@${polygon.id}/safeTransferFrom?${parameters.toString()}`;
      try {
        const dataUrl = await toDataURL(payload, { errorCorrectionLevel: "M", margin: 1, width: 280 });
        if (active) {
          setDepositQrPayload(payload);
          setDepositQrCode(dataUrl);
        }
      } catch {
        if (active) {
          setDepositQrPayload(undefined);
          setDepositQrCode(undefined);
        }
      }
    };
    void buildQrCode();
    return () => { active = false; };
  }, [address, depositAmount, depositStandard, depositToken, depositTokenId, selectedVault]);

  const copyVaultAddress = async () => {
    if (!selectedVault) return;
    try {
      await navigator.clipboard.writeText(selectedVault);
      setCopiedVault(true);
      toast({ title: "Vault address copied", description: selectedVault });
      window.setTimeout(() => setCopiedVault(false), 2000);
    } catch {
      toast({ title: "Copy failed", description: "Select and copy the vault address manually.", variant: "destructive" });
    }
  };

  const mintPersonalCollection = async () => {
    if (!INCEPTION_FACTORY_ADDRESS) {
      toast({ title: "Inception Tokens are not configured", description: "Add the Polygon factory address to this deployment.", variant: "destructive" });
      return;
    }
    setIsMinting(true);
    try {
      const { account, client, reader } = await requireWallet();
      const request = await reader.simulateContract({
        address: INCEPTION_FACTORY_ADDRESS,
        abi: INCEPTION_FACTORY_ABI,
        functionName: "create",
        args: [zeroAddress, "", "", zeroAddress, zeroAddress],
        account,
      });
      const hash = await client.writeContract(request.request);
      toast({ title: "Mint submitted", description: "Waiting for Polygon confirmation…" });
      const receipt = await reader.waitForTransactionReceipt({ hash });
      if (receipt.status !== "success") throw new Error("The Inception Token mint reverted on Polygon.");
      for (const receiptLog of receipt.logs) {
        const log = receiptLog as unknown as { address: Address; data: `0x${string}`; topics: [`0x${string}`, ...`0x${string}`[]] };
        if (log.address.toLowerCase() !== INCEPTION_FACTORY_ADDRESS.toLowerCase()) continue;
        try {
          const decoded = decodeEventLog({ abi: INCEPTION_FACTORY_ABI, data: log.data, topics: log.topics }) as {
            eventName: string;
            args: { collectionAddress: Address };
          };
          if (decoded.eventName === "NewCollection") setSelectedVault(decoded.args.collectionAddress);
        } catch { /* Ignore unrelated factory logs. */ }
      }
      await loadVaults();
      toast({ title: "Inception Token minted", description: "Your wallet now controls its dedicated asset vault." });
    } catch (error) {
      toast({ title: "Mint failed", description: transactionMessage(error), variant: "destructive" });
    } finally {
      setIsMinting(false);
    }
  };

  const depositAsset = async () => {
    if (!selectedVault || !isAddress(depositToken)) {
      toast({ title: "Check the deposit", description: "Select a vault and enter a valid token contract address.", variant: "destructive" });
      return;
    }
    setIsTransacting(true);
    try {
      const tokenId = BigInt(depositTokenId);
      const amount = BigInt(depositAmount || "1");
      const { account, client, reader } = await requireWallet();
      let hash: `0x${string}`;
      if (depositStandard === "erc721") {
        const request = await reader.simulateContract({
          address: depositToken,
          abi: ERC721_TRANSFER_ABI,
          functionName: "safeTransferFrom",
          args: [account, selectedVault, tokenId],
          account,
        });
        hash = await client.writeContract(request.request);
      } else {
        const request = await reader.simulateContract({
          address: depositToken,
          abi: ERC1155_TRANSFER_ABI,
          functionName: "safeTransferFrom",
          args: [account, selectedVault, tokenId, amount, "0x"],
          account,
        });
        hash = await client.writeContract(request.request);
      }
      await reader.waitForTransactionReceipt({ hash });
      await loadAssets();
      toast({ title: "Asset deposited", description: "The asset is now recorded in the selected vault." });
    } catch (error) {
      toast({ title: "Deposit failed", description: transactionMessage(error), variant: "destructive" });
    } finally {
      setIsTransacting(false);
    }
  };

  const withdrawAsset = async (asset: VaultAsset) => {
    if (!selectedVault || !isAddress(withdrawRecipient)) {
      toast({ title: "Recipient required", description: "Enter a valid Polygon recipient address.", variant: "destructive" });
      return;
    }
    if (asset.standard === "Unknown") {
      toast({ title: "Unsupported asset", description: "This token does not report ERC-721 or ERC-1155 support.", variant: "destructive" });
      return;
    }
    setIsTransacting(true);
    try {
      const { account, client, reader } = await requireWallet();
      const key = `${asset.token}:${asset.tokenId}`;
      let hash: `0x${string}`;
      if (asset.standard === "ERC-721") {
        const request = await reader.simulateContract({
          address: selectedVault,
          abi: INCEPTION_ERC721_WITHDRAW_ABI,
          functionName: "safeTransferTo",
          args: [asset.token, asset.tokenId, withdrawRecipient],
          account,
        });
        hash = await client.writeContract(request.request);
      } else {
        const request = await reader.simulateContract({
          address: selectedVault,
          abi: INCEPTION_ERC1155_WITHDRAW_ABI,
          functionName: "safeTransferTo",
          args: [asset.token, asset.tokenId, withdrawRecipient, BigInt(withdrawAmounts[key] || asset.balance || 1n)],
          account,
        });
        hash = await client.writeContract(request.request);
      }
      await reader.waitForTransactionReceipt({ hash });
      await loadAssets();
      toast({ title: "Asset withdrawn", description: "The vault transferred the asset to the recipient." });
    } catch (error) {
      toast({ title: "Withdrawal failed", description: transactionMessage(error), variant: "destructive" });
    } finally {
      setIsTransacting(false);
    }
  };

  const withdrawEverything = async (kind: "native" | "erc20" | "nfts") => {
    if (!selectedVault || !supportsRecovery || !isAddress(withdrawRecipient)) {
      toast({ title: "Recovery unavailable", description: "Select a recovery-capable vault and enter a valid recipient.", variant: "destructive" });
      return;
    }
    if (kind === "erc20" && !isAddress(erc20Token)) {
      toast({ title: "Token required", description: "Enter a valid ERC-20 contract address.", variant: "destructive" });
      return;
    }
    setIsTransacting(true);
    try {
      const { account, client, reader } = await requireWallet();
      const functionName = kind === "native" ? "withdrawAllNative" : kind === "erc20" ? "withdrawAllERC20" : "withdrawAllNFTs";
      const args = kind === "erc20" ? [erc20Token as Address, withdrawRecipient] : [withdrawRecipient];
      const request = await reader.simulateContract({
        address: selectedVault,
        abi: INCEPTION_VAULT_ABI,
        functionName,
        args,
        account,
      } as never);
      const hash = await client.writeContract(request.request);
      const receipt = await reader.waitForTransactionReceipt({ hash });
      if (receipt.status !== "success") throw new Error("The vault recovery transaction reverted on Polygon.");
      await Promise.all([loadAssets(), loadErc20Balance()]);
      toast({ title: "Withdrawal complete", description: `The vault transferred all selected ${kind === "nfts" ? "NFTs" : kind === "erc20" ? erc20Symbol : "POL"} to the recipient.` });
    } catch (error) {
      toast({ title: "Withdrawal failed", description: transactionMessage(error), variant: "destructive" });
    } finally {
      setIsTransacting(false);
    }
  };

  const registerVaultDomain = async () => {
    if (!selectedVault || !POLYGON_NAME_SERVICE_ADDRESS) {
      toast({ title: "Domain registration unavailable", description: "Select a vault and configure the Polygon Name Service address.", variant: "destructive" });
      return;
    }
    setIsTransacting(true);
    try {
      const label = validateDomainLabel(domainLabel);
      const { account, client, reader } = await requireWallet();
      const [registrationPrice, currentOwner, registryCode, registryTld] = await Promise.all([
        reader.readContract({ address: POLYGON_NAME_SERVICE_ADDRESS, abi: POLYGON_NAME_SERVICE_ABI, functionName: "price", args: [label] } as never) as Promise<bigint>,
        reader.readContract({ address: POLYGON_NAME_SERVICE_ADDRESS, abi: POLYGON_NAME_SERVICE_ABI, functionName: "getAddress", args: [label] } as never) as Promise<Address>,
        reader.getBytecode({ address: POLYGON_NAME_SERVICE_ADDRESS }),
        reader.readContract({ address: POLYGON_NAME_SERVICE_ADDRESS, abi: POLYGON_NAME_SERVICE_ABI, functionName: "tld" } as never) as Promise<string>,
      ]);
      if (!registryCode || registryTld !== POLYGON_NAME_TLD) throw new Error("The configured Polygon Name Service contract failed verification.");
      if (currentOwner !== zeroAddress) throw new Error(`${label}${POLYGON_NAME_TLD} is already registered.`);
      const request = await reader.simulateContract({
        address: selectedVault,
        abi: INCEPTION_VAULT_ABI,
        functionName: "externalCall",
        args: [POLYGON_NAME_SERVICE_ADDRESS, encodeDomainRegistration(label, domainRecord)],
        account,
        value: registrationPrice,
      });
      const hash = await client.writeContract(request.request);
      const receipt = await reader.waitForTransactionReceipt({ hash });
      if (receipt.status !== "success") throw new Error("The domain registration reverted on Polygon.");
      setDomainLabel("");
      setDomainRecord("");
      await loadAssets();
      toast({ title: "Domain registered", description: `${label}${POLYGON_NAME_TLD} is now owned by this Inception vault.` });
    } catch (error) {
      toast({ title: "Domain registration failed", description: transactionMessage(error), variant: "destructive" });
    } finally {
      setIsTransacting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 pb-16 pt-24">
        <div className="mx-auto max-w-5xl space-y-8">
          <div>
            <div className="mb-3 flex items-center gap-2 text-primary"><Archive className="h-5 w-5" /><span className="text-sm font-medium">Polygon asset vaults</span></div>
            <h1 className="text-3xl font-bold">Inception Tokens</h1>
            <p className="mt-3 max-w-2xl text-muted-foreground">Mint an ownership NFT with a dedicated smart-contract vault, then manage its ERC-721 and ERC-1155 contents from Matic Moon.</p>
          </div>

          <Alert>
            <Archive className="h-4 w-4" />
            <AlertTitle>The token is the vault key</AlertTitle>
            <AlertDescription>Transferring or approving an Inception Token can transfer control of everything in its vault. Review the contents first.</AlertDescription>
          </Alert>

          <HybridIdentityPanel />

          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader><CardTitle>Mint for yourself</CardTitle><CardDescription>Your connected wallet receives the Inception Token and controls its vault.</CardDescription></CardHeader>
              <CardContent>
                <Button className="w-full" onClick={mintPersonalCollection} disabled={isMinting || !INCEPTION_FACTORY_ADDRESS}>
                  {isMinting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{isMinting ? "Minting…" : "Mint Inception Token"}
                </Button>
                {!INCEPTION_FACTORY_ADDRESS && <p className="mt-3 text-xs text-destructive">The Inception factory is not configured.</p>}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Building2 className="h-5 w-5" />Mint for a DAO</CardTitle><CardDescription>The DAO treasury becomes the token and vault owner.</CardDescription></CardHeader>
              <CardContent>
                <Button className="w-full" variant="outline" onClick={() => navigate("/dao-it")}><Vote className="mr-2 h-4 w-4" />Open DAO manager</Button>
                <p className="mt-3 text-xs text-muted-foreground">Create, vote on, queue, and execute a “Mint DAO Inception Token” proposal.</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div><CardTitle>Manage your vaults</CardTitle><CardDescription>Vault ownership is read directly from Polygon.</CardDescription></div>
                <Button size="sm" variant="outline" onClick={loadVaults} disabled={isLoadingVaults}><RefreshCw className={`mr-2 h-4 w-4 ${isLoadingVaults ? "animate-spin" : ""}`} />Refresh</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {!isConnected ? <p className="text-sm text-muted-foreground">Connect your wallet to find its Inception Tokens.</p> : vaults.length === 0 ? <p className="text-sm text-muted-foreground">This wallet does not own an Inception Token yet.</p> : (
                <div className="space-y-2">
                  <Label htmlFor="vault">Inception vault</Label>
                  <select id="vault" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={selectedVault} onChange={(event) => setSelectedVault(event.target.value as Address)}>
                    {vaults.map((vault, index) => <option value={vault} key={vault}>Token {index + 1} — {vault}</option>)}
                  </select>
                  {selectedVault && <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="outline" onClick={copyVaultAddress}>{copiedVault ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}{copiedVault ? "Copied" : "Copy vault address"}</Button>
                    <a className="inline-flex items-center text-xs text-primary hover:underline" href={`https://polygonscan.com/address/${selectedVault}`} target="_blank" rel="noreferrer">View vault on PolygonScan <ExternalLink className="ml-1 h-3 w-3" /></a>
                  </div>}
                </div>
              )}

              {selectedVault && (
                <div className="grid gap-6 border-t pt-6 lg:grid-cols-2">
                  <div className="space-y-4">
                    {supportsAgentAccount && <><SessionKeyPanel vault={selectedVault} /><TrustActionsPanel vault={selectedVault} /></>}
                    <div><h3 className="font-medium">Deposit an NFT</h3><p className="text-xs text-muted-foreground">The connected wallet must own the asset.</p></div>
                    <div className="space-y-2"><Label htmlFor="deposit-standard">Token standard</Label><select id="deposit-standard" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={depositStandard} onChange={(event) => setDepositStandard(event.target.value as "erc721" | "erc1155")}><option value="erc721">ERC-721</option><option value="erc1155">ERC-1155</option></select></div>
                    <div className="space-y-2"><Label htmlFor="deposit-token">Token contract</Label><Input id="deposit-token" value={depositToken} onChange={(event) => setDepositToken(event.target.value)} placeholder="0x…" /></div>
                    <div className="grid grid-cols-2 gap-3"><div className="space-y-2"><Label htmlFor="deposit-id">Token ID</Label><Input id="deposit-id" value={depositTokenId} onChange={(event) => setDepositTokenId(event.target.value)} inputMode="numeric" /></div>{depositStandard === "erc1155" && <div className="space-y-2"><Label htmlFor="deposit-amount">Amount</Label><Input id="deposit-amount" value={depositAmount} onChange={(event) => setDepositAmount(event.target.value)} inputMode="numeric" /></div>}</div>
                    <Button onClick={depositAsset} disabled={isTransacting}><ArrowDownToLine className="mr-2 h-4 w-4" />Deposit into vault</Button>
                    <div className="rounded-md border border-dashed p-4">
                      <div className="mb-3 flex items-center gap-2"><QrCode className="h-4 w-4" /><h4 className="text-sm font-medium">Safe-transfer QR</h4></div>
                      {depositQrCode && depositQrPayload ? <div className="space-y-3 text-center">
                        <img className="mx-auto h-56 w-56 rounded bg-white p-2" src={depositQrCode} alt={`QR code to safely deposit into ${selectedVault}`} />
                        <p className="text-xs text-muted-foreground">Scan with an EIP-681-compatible Polygon wallet using the connected account. Confirm the token contract, ID, amount, and vault before signing.</p>
                        <Button size="sm" variant="ghost" onClick={() => navigator.clipboard.writeText(depositQrPayload)}><Copy className="mr-2 h-3.5 w-3.5" />Copy transaction link</Button>
                      </div> : <p className="text-xs text-muted-foreground">Enter a valid token contract and token ID to generate a `safeTransferFrom` transaction QR code.</p>}
                    </div>
                    <div className="space-y-3 rounded-md border p-4">
                      <div><h3 className="font-medium">Create a Polygon domain</h3><p className="text-xs text-muted-foreground">The domain NFT is minted directly to this vault and follows the Inception Token owner.</p></div>
                      <div className="flex items-center gap-2"><Input value={domainLabel} onChange={(event) => setDomainLabel(event.target.value)} placeholder="community" /><span className="text-sm text-muted-foreground">{POLYGON_NAME_TLD}</span></div>
                      <Textarea value={domainRecord} onChange={(event) => setDomainRecord(event.target.value)} placeholder="Optional initial record: ipfs://…, https://…, or metadata" rows={3} />
                      <p className="text-xs text-muted-foreground">Maximum {MAX_DOMAIN_RECORD_BYTES} UTF-8 bytes.</p>
                      <Button onClick={registerVaultDomain} disabled={isTransacting || !POLYGON_NAME_SERVICE_ADDRESS}>Register domain to vault</Button>
                      {!POLYGON_NAME_SERVICE_ADDRESS && <p className="text-xs text-destructive">Polygon Name Service is not configured for this deployment.</p>}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start justify-between"><div><h3 className="font-medium">Vault contents</h3><p className="text-xs text-muted-foreground">{assets.length} recorded NFT asset{assets.length === 1 ? "" : "s"}</p></div><Button size="icon" variant="ghost" onClick={loadAssets} disabled={isLoadingAssets}><RefreshCw className={`h-4 w-4 ${isLoadingAssets ? "animate-spin" : ""}`} /></Button></div>
                    {!supportsRecovery && <Alert variant="destructive"><AlertTitle>Legacy vault</AlertTitle><AlertDescription>This deployed vault cannot withdraw native POL or ERC-20 tokens. A version 2 factory deployment is required for the new recovery controls.</AlertDescription></Alert>}
                    <div className="space-y-2"><Label htmlFor="recipient">Withdrawal recipient</Label><Input id="recipient" value={withdrawRecipient} onChange={(event) => setWithdrawRecipient(event.target.value)} placeholder="0x…" /></div>
                    <div className="rounded-md border p-3">
                      <div className="flex items-center justify-between gap-3"><span className="font-medium">Native POL</span><span>{formatEther(nativeBalance)} POL</span></div>
                      {nativeBalance > 0n && !supportsRecovery && <p className="mt-2 text-xs text-destructive">This legacy vault cannot withdraw native POL already sent to it. Do not send additional POL to this address.</p>}
                      {supportsRecovery && nativeBalance > 0n && <Button className="mt-3" size="sm" variant="outline" onClick={() => withdrawEverything("native")} disabled={isTransacting}><ArrowUpFromLine className="mr-2 h-4 w-4" />Withdraw all POL</Button>}
                    </div>
                    <div className="space-y-3 rounded-md border p-3">
                      <div><Label htmlFor="erc20-token">ERC-20 token contract</Label><Input id="erc20-token" className="mt-2" value={erc20Token} onChange={(event) => setErc20Token(event.target.value)} placeholder="0x…" /></div>
                      {isAddress(erc20Token) && <p className="text-xs text-muted-foreground">Vault balance: {erc20Balance === undefined ? "Unable to read token" : `${formatUnits(erc20Balance, erc20Decimals)} ${erc20Symbol}`}</p>}
                      {supportsRecovery && erc20Balance !== undefined && erc20Balance > 0n && <Button size="sm" variant="outline" onClick={() => withdrawEverything("erc20")} disabled={isTransacting}><ArrowUpFromLine className="mr-2 h-4 w-4" />Withdraw all {erc20Symbol}</Button>}
                    </div>
                    {supportsRecovery && assets.length > 0 && <Button variant="outline" onClick={() => withdrawEverything("nfts")} disabled={isTransacting}><ArrowUpFromLine className="mr-2 h-4 w-4" />Withdraw all recorded NFTs</Button>}
                    {isLoadingAssets ? <p className="text-sm text-muted-foreground">Loading assets…</p> : assets.length === 0 ? <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">This vault has no recorded NFT assets.</p> : assets.map((asset) => {
                      const key = `${asset.token}:${asset.tokenId}`;
                      return <div className="space-y-3 rounded-md border p-3" key={key}>
                        <div className="flex items-start justify-between gap-3"><div><p className="font-medium">{asset.standard} #{asset.tokenId.toString()}</p><a className="text-xs text-primary hover:underline" href={`https://polygonscan.com/token/${asset.token}?a=${asset.tokenId}`} target="_blank" rel="noreferrer">{compactAddress(asset.token)}</a></div>{asset.balance !== undefined && <span className="text-xs text-muted-foreground">Balance: {asset.balance.toString()}</span>}</div>
                        {asset.standard === "ERC-1155" && <Input value={withdrawAmounts[key] ?? asset.balance?.toString() ?? "1"} onChange={(event) => setWithdrawAmounts((current) => ({ ...current, [key]: event.target.value }))} inputMode="numeric" placeholder="Amount" />}
                        <Button size="sm" variant="outline" onClick={() => withdrawAsset(asset)} disabled={isTransacting || asset.standard === "Unknown"}><ArrowUpFromLine className="mr-2 h-4 w-4" />Withdraw</Button>
                      </div>;
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {isConnected && vaults.length > 0 && (
            <NestedOwnershipPanel
              roots={vaults}
              onChanged={async () => { await Promise.all([loadVaults(), loadAssets()]); }}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default InceptionTokens;
